import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import {
    Box,
    Button,
    Dialog,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { ReceiptText, User } from "lucide-react";
import { useForm } from "react-hook-form";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { useModalStyles } from "../../common/useModalStyles";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { fetchPracticeLookupByUserRole } from "../../../redux/slice/commonSlice";
import { USER_ROLE_ENTITY_NAMES } from "../../../constants/applicationConstants";
import { selectIsValueFoundInToken, useAuthSlice } from "../../../redux/slice/authSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { OpenInNew } from "@mui/icons-material";
import { fetchAllUsers, useUserReducer } from "../../../redux/slice/userSlice";
import { fullName } from "../../../utils/Utility";

interface ManagePracticeModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: userFormData) => void;
}
export type userFormData = {
    user: string | number;
}

const ManageUserModal = ({ open, onClose, onSubmit }: ManagePracticeModalProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {
        tokens,
        paperSx,
        headerSx,
        iconBoxSx,
        bodySx,
        footerSx,
        closeIconSx,
        dropdownWrapSx,
        primaryBtnSx,
        cancelBtnSx,
    } = useModalStyles();

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        getValues,
        formState: { errors },
    } = useForm<userFormData>({
        defaultValues: {
            user: ""
        },
        mode: "onSubmit",
    });

    const dispatch = useAppDispatch();
    const { userInfo } = useAuthSlice();
    const { allUsers } = useUserReducer();

    const handleClose = () => {
        setTimeout(() => {
            reset();
            resetInvoiceData();
            onClose();
        }, 300);
    };
    const onFormSubmit = (data: userFormData) => {
        onSubmit(data);
        handleClose();
    };

    const FieldLabel = ({
        label,
        required = false,
    }: {
        label: string;
        required?: boolean;
    }) => (
        <Typography
            sx={{
                fontSize: "0.8rem",
                fontWeight: 600,
                color: tokens.textSecondary,
                mb: 0.7,
                display: "flex",
                alignItems: "center",
                gap: 0.3,
            }}
        >
            {label}
            {required && (
                <Box component="span" sx={{ color: tokens.accent }}>
                    *
                </Box>
            )}
        </Typography>
    );

    type Option = {
        value: string | number;
        label: string;
    };

    const handleUserChange = async () => {
        const practice = getValues('user');
    };

    const userOptions = useMemo(() => {
        let options: Option[] = [];
        allUsers.map((user) => {
            const option: Option = {
                label: `${fullName("", user.firstName, user.middleName, user.lastName)} (${user.email})`,
                value: user.userId,
            };
            options.push(option);
        }
        );
        if (allUsers && allUsers?.length == 1) {
            setValue('user', allUsers[0].userId);
        }
        return options;
    }, [allUsers]);

    useEffect(() => {
        if (open) fetchUsers();
        else handleClose();
    }, [open]);

    const fetchUsers = async () => {
        const response = await dispatch(fetchAllUsers(false));

        if (response.meta.requestStatus === "fulfilled" && response.payload) {
            const users = response.payload;

            if (
                userInfo?.entityName === USER_ROLE_ENTITY_NAMES.PRACTICE &&
                users.length === 1
            ) {
                setValue("user", users[0].practiceId);
            }
        }
    };

    const resetInvoiceData = () => {
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{ sx: paperSx(isMobile) }}
        >
            <form onSubmit={handleSubmit(onFormSubmit)}>
                <Box sx={headerSx}>
                    <Box sx={iconBoxSx(tokens.accent)}>
                        <User size={20} color={tokens.accent} strokeWidth={1.8} />
                    </Box>
                    <Box>
                        <Typography
                            fontWeight={700}
                            sx={{
                                fontSize: "1.1rem",
                                color: tokens.textPrimary,
                                lineHeight: 1.3,
                            }}
                        >
                            Manage User
                        </Typography>
                        <Typography
                            sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}
                        >
                            Select a user to view or edit
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small" sx={closeIconSx}>
                        <CloseIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                </Box>

                <Box sx={bodySx}>
                    <Box>
                        <FieldLabel label="User" required />
                        <Box sx={dropdownWrapSx()}>
                            <CustomDropdownField
                                name="user"
                                control={control}
                                options={userOptions}
                                size="small"
                                placeholderText={
                                    userOptions.length === 0
                                        ? "No users found"
                                        : "Choose a user..."
                                }
                                sortOptions={false}
                                disabled={userOptions?.length === 1}
                                onChangeSelect={() => handleUserChange()}
                                rules={{ required: "Please select a user to continue." }}
                            />
                        </Box>
                    </Box>
                </Box>

                <Box sx={footerSx}>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            type='submit'
                            startIcon={
                                <OpenInNew sx={{ fontSize: "1rem !important" }} />
                            }
                            // onClick={}
                            sx={primaryBtnSx}
                        >
                            Manage User
                        </Button>

                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
                            onClick={handleClose}
                            sx={cancelBtnSx}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </form>
        </Dialog>
    );
};

export default ManageUserModal;
